"use client";

import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { SlHome, SlLocationPin } from "react-icons/sl";
import * as v1Api from "../../services/api";
import { CleaningCategories } from "../BookingApp/interfaces";
import { useState, useRef, FormEvent, useEffect } from "react";
import { useRouter } from "next/router";
import { sendGTMEvent } from "@/lib/gtm";

interface GooglePlace {
  formatted_address?: string;
  address_components?: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
}

interface CallToActionForm {
  pageCategory?: string;
  textColor?: string;
}

interface AddressAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  onFocus?: () => void;
}

interface AddressAutocompleteFieldProps extends AddressAutocompleteInputProps {
  onLoad: (autocomplete: google.maps.places.Autocomplete) => void;
  onPlaceChanged: () => void;
}

function AddressInput({
  value,
  onChange,
  onBlur,
  onFocus,
}: AddressAutocompleteInputProps) {
  return (
    <input
      className="pb-3 mt-2 border-b border-[#44A0ED] text-[16px] outline-none w-full bg-transparent placeholder:text-inherit"
      type="text"
      placeholder="Type address"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onFocus={onFocus}
    />
  );
}

function AddressAutocompleteField({
  value,
  onChange,
  onBlur,
  onLoad,
  onPlaceChanged,
}: AddressAutocompleteFieldProps) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  if (!isLoaded) {
    return <AddressInput value={value} onChange={onChange} onBlur={onBlur} />;
  }

  return (
    <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
      <AddressInput value={value} onChange={onChange} onBlur={onBlur} />
    </Autocomplete>
  );
}

export default function CallToActionForm({
  pageCategory,
  textColor,
}: CallToActionForm) {
  const router = useRouter();
  const resolvedTextColor = textColor ?? "#9B9B9B";
  const [shouldLoadGoogleMaps, setShouldLoadGoogleMaps] = useState(false);

  // Load Categories -----------------------------------------------------------
  const [categories, setCategories] = useState<CleaningCategories>({});

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await v1Api.fetchCleaningCategories();
        setCategories(data);
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    };

    loadCategories();
  }, []);

  // ---------------------------------------------------------------------------

  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    pageCategory
  );

  const [formatedAddress, setFormatedAddress] = useState("");
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const handlePlaceSelect = () => {
    const place: GooglePlace | undefined = autocompleteRef.current?.getPlace();
    let streetNumber = "";
    let route = "";
    let nextCity = "";
    let nextState = "";
    let nextZipCode = "";

    if (
      place?.address_components &&
      Array.isArray(place.address_components) &&
      place.formatted_address
    ) {
      for (const component of place.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case "street_number":
            streetNumber = component.long_name;
            break;
          case "route":
            route = component.long_name;
            break;
          case "locality":
            nextCity = component.long_name;
            break;
          case "administrative_area_level_1":
            nextState = component.short_name;
            break;
          case "postal_code":
            nextZipCode = component.long_name;
            break;
        }
      }

      const nextAddress = `${streetNumber} ${route}`.trim();
      setFormatedAddress(place.formatted_address);
      setAddress(nextAddress);
      setCity(nextCity);
      setState(nextState);
      setZipCode(nextZipCode);

      const addressError = validateAddressFields({
        address: nextAddress,
        city: nextCity,
        state: nextState,
        zipCode: nextZipCode,
      });
      setErrors((prev) => ({ ...prev, address: addressError ?? undefined }));
    } else {
      const addressError = validateAddressFields();
      setErrors((prev) => ({ ...prev, address: addressError ?? undefined }));
    }
  };

  const handleBlur = () => {
    console.log("handleBlur");
    if (autocompleteRef.current) {
      handlePlaceSelect();
    }
    const addressError = validateAddressFields();
    setErrors((prev) => ({ ...prev, address: addressError ?? undefined }));
  };

  const [errors, setErrors] = useState<{ address?: string; category?: string }>(
    {}
  );

  const getMissingAddressFields = (overrides?: {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  }) => {
    const missing: string[] = [];

    const addressValue = overrides?.address ?? address;
    const cityValue = overrides?.city ?? city;
    const stateValue = overrides?.state ?? state;
    const zipCodeValue = overrides?.zipCode ?? zipCode;

    if (!addressValue.trim()) missing.push("address");
    if (!cityValue.trim()) missing.push("city");
    if (!stateValue.trim()) missing.push("state");
    if (!zipCodeValue.trim()) missing.push("zip code");

    return missing;
  };

  const validateAddressFields = (overrides?: {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  }): string | null => {
    const missing = getMissingAddressFields(overrides);
    if (missing.length === 0) {
      return null;
    }

    return `Missing: ${missing.join(", ")}. Please check your address.`;
  };

  const validateForm = (): boolean => {
    const newErrors: { address?: string; category?: string } = {};

    const addressError = validateAddressFields();
    if (addressError) {
      newErrors.address = addressError;
    }

    if (!selectedCategory) {
      newErrors.category = "Please select a cleaning type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateForm()) {
      const formData: Record<string, string> = {
        address,
        address2,
        city,
        state,
        zipCode,
        selectedCategory: selectedCategory || "",
      };
      const queryString = new URLSearchParams(formData).toString();
      sendGTMEvent({ event: "SubmitCTA", value: selectedCategory });

      router.push(`/services?${queryString}`);
    } else {
      console.log("Form validation failed. Errors:", errors);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full mx-auto"
      style={{ color: resolvedTextColor }}
    >
      <div className="grid md:grid-cols-3 gap-10">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-yellow-700">
              <SlLocationPin />
            </span>
            <label htmlFor="address" className="text-[18px] tracking-[5px] my-2">
              ADDRESS
            </label>
          </div>
          {shouldLoadGoogleMaps ? (
            <AddressAutocompleteField
              value={formatedAddress}
              onChange={setFormatedAddress}
              onBlur={handleBlur}
              onLoad={(autocomplete) => {
                autocompleteRef.current = autocomplete;
                autocomplete.setComponentRestrictions({ country: "us" });
              }}
              onPlaceChanged={handlePlaceSelect}
            />
          ) : (
            <AddressInput
              value={formatedAddress}
              onChange={setFormatedAddress}
              onBlur={handleBlur}
              onFocus={() => setShouldLoadGoogleMaps(true)}
            />
          )}
          {errors.address && (
            <span className="text-red-500 text-sm mt-1">{errors.address}</span>
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-yellow-700">
              <SlHome />
            </span>
            <label htmlFor="cleaning-category" className="text-[18px] tracking-[5px] my-2">
              CLEANING CATEGORY
            </label>
          </div>
          <select
            id="cleaning-category"
            onChange={(e) => setSelectedCategory(e.target.value)}
            value={selectedCategory}
            className="pb-3 mt-3 border-b border-[#44A0ED] text-[16px] outline-none w-full bg-transparent placeholder:text-inherit"
          >
            <option value="">Select cleaning category</option>
            {Object.entries(categories).map(([key, category]) => (
              <option key={key} value={key}>
                {category.name}
              </option>
            ))}
          </select>
          {errors.category && (
            <span className="text-red-500 text-sm mt-1">{errors.category}</span>
          )}
        </div>
        <button className="md:max-w-[100%] bg-[#44A0ED] text-white text-[16px] px-6 h-14 self-end">
          GET A FREE ESTIMATE
        </button>
      </div>
    </form>
  );
}
